/**
 * Wave 3.16 — Receive Jewellery from Goldsmith wizard.
 *
 * Mounted at /orders/:id/receive-jewellery. Drives a four-step flow
 * that follows the user-defined narrative:
 *
 *   Step 1 — Image + Gross Weight + Jewellery No/Title
 *   Step 2 — Raw Material Consumption (with overflow routing)
 *   Step 3 — Labour Rate, Gold Loss, and (if needed) Shortage Resolver
 *   Step 4 — Preview & Confirm
 *
 * The wizard stays a single component (vs. nested routes) so the four
 * steps share state without prop-drilling. State only commits to the
 * backend on the final Confirm action.
 *
 * The Gold Shortage Resolver (Step 3) intercepts the failure case where
 * the goldsmith's *attributed* gold for this order is less than what
 * the jewellery requires (Net Weight + optional Gold Loss in grams).
 * The user is offered two resolution paths:
 *
 *   A. Move gold from the goldsmith's *unattributed* pool onto this
 *      order  (`POST /api/goldsmith-issues/attribute`).
 *   B. Issue fresh gold from stock to the goldsmith and attribute it
 *      to this order in one shot
 *      (`POST /api/goldsmith-issues/`).
 *
 * After either resolution succeeds, the wizard re-fetches the preview
 * and recomputes derived totals, unblocking the Confirm action.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { sortDiamondItems } from '@/lib/diamondHierarchy';
import {
  RadioGroup, RadioGroupItem,
} from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, Gem, Sparkles, Circle, Coins,
  Upload, Image as ImageIcon, Calculator, AlertTriangle,
  Wrench, PackagePlus, StickyNote,
} from 'lucide-react';
import { formatINR, formatCarats, formatGrams } from '@/lib/format';
import { extractError } from '@/lib/errors';

const MAT_ICON = {
  diamond: Gem,
  color_stone: Sparkles,
  pearl: Circle,
  gold: Coins,
};
const MAT_LABEL = {
  diamond: 'Diamond',
  color_stone: 'Colour Stone',
  pearl: 'Pearl',
  gold: 'Gold',
};

const STEPS = [
  { key: 'capture',     label: 'Image & Gross Weight' },
  { key: 'consumption', label: 'Raw Material Used' },
  { key: 'labour',      label: 'Labour & Gold Loss' },
  { key: 'preview',     label: 'Preview & Confirm' },
];

export default function ReceiveJewellery() {
  const navigate = useNavigate();
  return (
    <AppShell
      title="Receive Jewellery from Goldsmith"
      subtitle="Capture finished piece, consume raw material, settle labour & gold loss"
      right={
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="h-9"
          data-testid="receive-jewellery-back-button"
        >
          <ArrowLeft size={14} className="mr-1" /> Cancel
        </Button>
      }
    >
      <CompanyGate>
        <ReceiveJewelleryInner />
      </CompanyGate>
    </AppShell>
  );
}

function ReceiveJewelleryInner() {
  const { id: orderId } = useParams();
  const { effectiveCompanyId } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);

  // ----- Step 1 — Capture (image + gross weight + header fields) ----------
  const [jewelleryNo, setJewelleryNo] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [useOrderImage, setUseOrderImage] = useState(true);
  const [grossWeight, setGrossWeight] = useState('');

  // ----- Step 2 — Consumption keyed by `${issue_id}|${line_id}` ------------
  // Each value: { consumed_carats, consumed_pieces, consumed_grams, overflow_destination }
  const [lineState, setLineState] = useState({});

  // ----- Step 3 — Labour + Gold Loss --------------------------------------
  const [labourRate, setLabourRate] = useState('');
  const [goldLossPercent, setGoldLossPercent] = useState('');
  const [goldLossMode, setGoldLossMode] = useState('');  // '' | 'gold_grams' | 'rupees'
  const [goldRate999, setGoldRate999] = useState('');
  // Wave 3.36 — Optional credit days the goldsmith offers on the labour bill.
  const [creditDays, setCreditDays] = useState('');

  // Gold shortage resolver state.
  const [shortageDialogOpen, setShortageDialogOpen] = useState(false);

  // ----- Wizard data loading ----------------------------------------------
  const fetchPreview = useCallback(async ({ preserveState = false } = {}) => {
    if (!effectiveCompanyId || !orderId) return;
    setLoading(true);
    try {
      const res = await api.get(`/jewellery/preview/${orderId}`, {
        params: { company_id: effectiveCompanyId },
      });
      setPreview(res.data);
      if (!preserveState) {
        setJewelleryNo(res.data.suggested_jewellery_no || '');
        setTitle(res.data.order?.title || '');
        if (res.data.gold_rate_999_snapshot) {
          setGoldRate999(String(res.data.gold_rate_999_snapshot));
        }
      }
      // Seed / refresh consumption defaults — preserves any user-entered
      // values while filling in newly added gold lines (e.g. after a
      // shortage resolution adds a fresh issue).
      setLineState((prev) => {
        const next = preserveState ? { ...prev } : {};
        for (const ln of res.data.lines || []) {
          const key = `${ln.issue_id}|${ln.line_id}`;
          if (preserveState && next[key]) continue;
          if (ln.material_type === 'gold') {
            next[key] = {
              consumed_grams: ln.attributed_grams || 0,
              consumed_carats: 0,
              consumed_pieces: 0,
              overflow_destination: 'unattributed_goldsmith',
            };
          } else {
            next[key] = {
              consumed_carats: ln.attributed_carats || 0,
              consumed_pieces: ln.attributed_pieces || 0,
              consumed_grams: 0,
              overflow_destination: 'unattributed_goldsmith',
              // Wave 4.x — Ghisai Loss (write-off) inputs; blank = none.
              ghisai_loss_carats: '',
              ghisai_loss_pieces: '',
            };
          }
        }
        return next;
      });
    } catch (e) {
      toast.error(extractError(e, 'Failed to load receive-jewellery preview'));
    } finally {
      setLoading(false);
    }
  }, [orderId, effectiveCompanyId]);

  useEffect(() => {
    fetchPreview({ preserveState: false });
  }, [fetchPreview]);

  // ----- Derived totals ----------------------------------------------------
  const lines = useMemo(() => preview?.lines || [], [preview]);
  const derived = useMemo(() => {
    let d_ct = 0, d_pcs = 0, cs_ct = 0, cs_pcs = 0, p_ct = 0, p_pcs = 0;
    let gold_src_in_order_purity = 0;
    let gold_available_in_order_purity = 0;
    const order_purity_pct = preview?.order?.gold_purity_percent || 0;
    for (const ln of lines) {
      const key = `${ln.issue_id}|${ln.line_id}`;
      const s = lineState[key] || {};
      if (ln.material_type === 'diamond') {
        d_ct += +s.consumed_carats || 0; d_pcs += +s.consumed_pieces || 0;
      } else if (ln.material_type === 'color_stone') {
        cs_ct += +s.consumed_carats || 0; cs_pcs += +s.consumed_pieces || 0;
      } else if (ln.material_type === 'pearl') {
        p_ct += +s.consumed_carats || 0; p_pcs += +s.consumed_pieces || 0;
      } else if (ln.material_type === 'gold') {
        const g = +s.consumed_grams || 0;
        if (g > 0 && order_purity_pct > 0) {
          gold_src_in_order_purity += g * (ln.purity_percent || 0) / order_purity_pct;
        }
        if (order_purity_pct > 0) {
          gold_available_in_order_purity +=
            (ln.attributed_grams || 0) * (ln.purity_percent || 0) / order_purity_pct;
        }
      }
    }
    const total_stone_ct = d_ct + cs_ct + p_ct;
    const stone_grams_eq = total_stone_ct / 5;
    const gross = +grossWeight || 0;
    const net = +(gross - stone_grams_eq).toFixed(3);
    // Wave 3.99ac — Mountings (semi-finished gold pieces) issued into this
    // order ADD to the net weight (they ARE gold) but the karigar did NOT
    // craft them. Their weight is therefore excluded from labour, gold loss
    // and the gold draw. Everything is charged on the labour-eligible
    // weight = Net − Mountings.
    const mounting_grams = +(preview?.mountings_grams_total || 0);
    const labour_eligible_net = +(Math.max(0, net - mounting_grams)).toFixed(3);
    const labour_amt = +(labour_eligible_net * (+labourRate || 0)).toFixed(2);
    const loss_pct = +goldLossPercent || 0;
    const loss_grams = loss_pct > 0 ? +(labour_eligible_net * loss_pct / 100).toFixed(3) : 0;
    const loss_g999 = +(loss_grams * order_purity_pct / 100).toFixed(3);
    // Wave 3.20 — Gold loss VALUE in ₹ is always computed when % > 0 (regardless
    // of settlement mode), because the company always bears that cost.
    // Wave 3.20.1 — Falls back to the snapshot rate when the user leaves the
    // rate field blank, mirroring server-side behaviour.
    const effective_rate_999 = +goldRate999 || +(preview?.gold_rate_999_snapshot || 0);
    const loss_value_inr = loss_pct > 0 ? +(loss_g999 * effective_rate_999).toFixed(2) : 0;
    // Bill to the goldsmith only includes the loss value if it's settled in cash.
    const loss_amt = (loss_pct > 0 && goldLossMode === 'rupees') ? loss_value_inr : 0;
    const total_amt = +(labour_amt + loss_amt).toFixed(2);
    // Product's actual labour cost (always includes loss value when declared).
    const total_product_labour_amt = +(labour_amt + loss_value_inr).toFixed(2);
    const expected_gold_draw = labour_eligible_net + (goldLossMode === 'gold_grams' ? loss_grams : 0);
    const submission_mismatch = Math.abs(expected_gold_draw - gold_src_in_order_purity) > 0.005;
    const shortage = Math.max(0, +(expected_gold_draw - gold_available_in_order_purity).toFixed(3));
    return {
      d_ct, d_pcs, cs_ct, cs_pcs, p_ct, p_pcs,
      stone_grams_eq, gross, net, mounting_grams, labour_eligible_net, labour_amt,
      loss_pct, loss_grams, loss_g999, loss_amt, loss_value_inr, total_amt, total_product_labour_amt,
      expected_gold_draw, gold_src_in_order_purity, gold_available_in_order_purity,
      submission_mismatch, shortage,
    };
  }, [lines, lineState, grossWeight, labourRate, goldLossPercent, goldLossMode, goldRate999, preview]);

  // Wave 3.20 — Auto-allocate Gold "consumed_grams" across attributed gold
  // lines. The user no longer enters gold consumption manually — the system
  // derives the required draw (Net Weight + loss-grams when goldsmith is
  // recovering the loss in physical gold) and distributes it FIFO across the
  // attributed gold lines (converting between source purity ↔ order purity
  // as it goes). Anything beyond the need becomes overflow (already routed
  // to the goldsmith's unattributed pool).
  useEffect(() => {
    const goldLines = lines.filter((ln) => ln.material_type === 'gold');
    if (goldLines.length === 0) return;
    const orderPct = preview?.order?.gold_purity_percent || 0;
    if (orderPct <= 0) return;
    const need = Math.max(0, derived.expected_gold_draw || 0);
    let remaining_op = need;  // grams remaining to allocate, expressed in order-purity
    const updates = {};
    for (const ln of goldLines) {
      const key = `${ln.issue_id}|${ln.line_id}`;
      const srcPct = ln.purity_percent || 0;
      if (srcPct <= 0) continue;
      const attrG = ln.attributed_grams || 0;
      const attrOp = attrG * srcPct / orderPct;   // attribution in order purity
      const takeOp = Math.min(remaining_op, attrOp);
      const takeSrc = +(takeOp * orderPct / srcPct).toFixed(3);
      updates[key] = takeSrc;
      remaining_op -= takeOp;
      if (remaining_op <= 0.0005) remaining_op = 0;
    }
    // Only call setLineState if at least one line actually changed (avoids
    // an unnecessary re-render loop).
    setLineState((prev) => {
      let dirty = false;
      const next = { ...prev };
      for (const [key, takeSrc] of Object.entries(updates)) {
        const cur = +((prev[key] || {}).consumed_grams || 0);
        if (Math.abs(cur - takeSrc) > 0.0005) {
          next[key] = { ...prev[key], consumed_grams: takeSrc };
          dirty = true;
        }
      }
      return dirty ? next : prev;
    });
    // We deliberately exclude `lineState` from deps to prevent a feedback
    // loop — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, preview?.order?.gold_purity_percent, derived.expected_gold_draw]);

  // ----- Per-step validation ----------------------------------------------
  const stepValid = useMemo(() => {
    // Step 1 — capture: just needs a positive gross weight.
    const captureOk = +grossWeight > 0;
    // Step 2 — consumption: every line within attribution bounds.
    const consumptionOk = lines.every((ln) => {
      const s = lineState[`${ln.issue_id}|${ln.line_id}`] || {};
      if (ln.material_type === 'gold') {
        return (+s.consumed_grams || 0) <= (ln.attributed_grams || 0) + 1e-6
          && (+s.consumed_grams || 0) >= 0;
      }
      return (
        (+s.consumed_carats || 0) <= (ln.attributed_carats || 0) + 1e-6
        && (+s.consumed_carats || 0) >= 0
        && (+s.consumed_pieces || 0) <= (ln.attributed_pieces || 0)
        && (+s.consumed_pieces || 0) >= 0
      );
    });
    // Step 3 — labour + gold loss + shortage cleared.
    // Wave 3.20 — Gold 999 rate is now required whenever loss > 0 (in BOTH
    // settlement modes) because product-cost reporting always values the loss.
    // Wave 3.20.1 — A missing rate is allowed when the system already has a
    // running-average snapshot we can fall back to; otherwise we require the
    // user to enter one.
    const hasRateFallback = +(preview?.gold_rate_999_snapshot || 0) > 0;
    const rateOk = +goldRate999 > 0 || hasRateFallback;
    const labourOk = (+labourRate || 0) >= 0
      && (derived.loss_pct === 0 || rateOk)
      && !derived.submission_mismatch
      && derived.shortage <= 0
      && derived.net >= 0;
    return { 0: captureOk, 1: consumptionOk, 2: labourOk, 3: true };
  }, [lines, lineState, grossWeight, derived, labourRate, goldLossMode, goldRate999]);

  // ----- Image upload helper ----------------------------------------------
  const handleImageFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2 MB or smaller');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageDataUrl(reader.result);
      setUseOrderImage(false);
    };
    reader.readAsDataURL(file);
  };

  // ----- Submit -----------------------------------------------------------
  const handleSubmit = async () => {
    if (!stepValid[0] || !stepValid[1] || !stepValid[2]) {
      toast.error('Some inputs are still invalid — fix them in the relevant step before confirming');
      return;
    }
    // Wave 4.x — Guard: Ghisai Loss can never exceed a line's overflow.
    for (const ln of lines) {
      if (ln.material_type === 'gold') continue;
      const s = lineState[`${ln.issue_id}|${ln.line_id}`] || {};
      const ovCt = Math.max(0, (ln.attributed_carats || 0) - (+s.consumed_carats || 0));
      const ovPcs = Math.max(0, (ln.attributed_pieces || 0) - (+s.consumed_pieces || 0));
      const glCt = +s.ghisai_loss_carats || 0;
      const glPcs = +s.ghisai_loss_pieces || 0;
      if (glCt < 0 || glPcs < 0) {
        toast.error(`Ghisai Loss cannot be negative (${ln.packet_no || ln.material_type})`);
        setStepIdx(2);
        return;
      }
      if (glCt - ovCt > 1e-6 || glPcs > ovPcs) {
        toast.error(`Ghisai Loss exceeds overflow on ${ln.packet_no || ln.material_type} — max ${ovCt.toFixed(2)} ct / ${ovPcs} pcs`);
        setStepIdx(2);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        company_id: effectiveCompanyId,
        source_order_id: orderId,
        jewellery_no: jewelleryNo || null,
        title: title || null,
        description: description || null,
        image_data_url: (!useOrderImage && imageDataUrl) ? imageDataUrl : null,
        gross_weight_grams: +grossWeight,
        diamond_carats: derived.d_ct,
        diamond_pieces: derived.d_pcs,
        color_stone_carats: derived.cs_ct,
        color_stone_pieces: derived.cs_pcs,
        pearl_carats: derived.p_ct,
        pearl_pieces: derived.p_pcs,
        labour_rate_per_gram: +labourRate || 0,
        gold_loss_percent: +goldLossPercent || 0,
        gold_loss_mode: derived.loss_pct > 0 ? (goldLossMode || null) : null,
        gold_rate_999_at_receipt: derived.loss_pct > 0 && goldLossMode === 'rupees'
          ? +goldRate999 : null,
        // Wave 3.36 — pass through optional credit days for the labour bill.
        credit_days: creditDays !== '' && Number(creditDays) > 0 ? Number(creditDays) : null,
        consumption: lines.map((ln) => {
          const s = lineState[`${ln.issue_id}|${ln.line_id}`] || {};
          return {
            issue_id: ln.issue_id,
            line_id: ln.line_id,
            material_type: ln.material_type,
            consumed_carats: +s.consumed_carats || 0,
            consumed_pieces: +s.consumed_pieces || 0,
            consumed_grams: +s.consumed_grams || 0,
            overflow_destination: s.overflow_destination || 'unattributed_goldsmith',
            // Wave 4.x — Ghisai Loss (stones only; write-off). Blank => 0.
            ghisai_loss_carats: ln.material_type === 'gold' ? 0 : (+s.ghisai_loss_carats || 0),
            ghisai_loss_pieces: ln.material_type === 'gold' ? 0 : (+s.ghisai_loss_pieces || 0),
          };
        }),
      };
      const res = await api.post('/jewellery', payload);
      toast.success(`Jewellery ${res.data.jewellery_no} received`);
      navigate(`/jewellery/${res.data.id}`);
    } catch (e) {
      // Intercept structured GOLD_SHORTAGE errors and prompt the resolver.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'GOLD_SHORTAGE') {
        toast.error('Gold shortage detected. Resolve via the panel below before confirming.');
        setStepIdx(2);
        setShortageDialogOpen(true);
      } else {
        toast.error(extractError(e, 'Failed to receive jewellery'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Empty state ------------------------------------------------------
  if (loading || !preview) {
    return (
      <div className="text-slate-500 py-10" data-testid="receive-jewellery-loading">
        Loading…
      </div>
    );
  }
  if (!lines.length) {
    return (
      <Card className="p-6 border-amber-200 bg-amber-50" data-testid="receive-jewellery-no-attribution">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-700" size={20} />
          <div>
            <div className="font-medium text-amber-900">No goldsmith attribution found for this order</div>
            <div className="text-sm text-amber-800 mt-1">
              You need to issue raw material to the goldsmith and attribute it to this order before you can receive the finished jewellery.
            </div>
            <Button className="mt-3" variant="outline" onClick={() => navigate(`/orders/${orderId}`)}>
              <ArrowLeft size={14} className="mr-1" /> Back to order
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // ===========================================================================
  // Step renderers
  // ===========================================================================
  const renderStepCapture = () => (
    <Card className="p-5" data-testid="receive-jewellery-step-capture">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-medium text-[var(--ink-navy)]">Step 1 · Image &amp; Gross Weight</div>
          <div className="text-xs text-slate-600 mt-0.5">
            Capture the finished piece. If you skip the image, the order&apos;s reference image is used for the BOM and detail view.
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* Image picker */}
        <div className="lg:col-span-5">
          <Label className="text-xs text-slate-600">Jewellery image</Label>
          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="aspect-square max-w-[260px] rounded-md overflow-hidden bg-white border border-slate-200 flex items-center justify-center">
              {(!useOrderImage && imageDataUrl) ? (
                <img
                  src={imageDataUrl}
                  alt="Jewellery"
                  className="w-full h-full object-cover"
                  data-testid="receive-jewellery-image-preview"
                />
              ) : (preview.order.images && (preview.order.images || []).find((x) => typeof x === 'string' && x.startsWith('data:image'))) ? (
                <img
                  src={(preview.order.images || []).find((x) => typeof x === 'string' && x.startsWith('data:image'))}
                  alt="Order"
                  className="w-full h-full object-cover opacity-80"
                />
              ) : (
                <div className="text-center text-slate-400">
                  <ImageIcon size={28} className="mx-auto mb-1" />
                  <div className="text-[11px]">No image yet</div>
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm" className="h-9" data-testid="receive-jewellery-upload-button">
                <label className="cursor-pointer flex items-center gap-1.5">
                  <Upload size={14} /> Upload image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageFile(e.target.files?.[0])} />
                </label>
              </Button>
              {(imageDataUrl) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 text-slate-500 hover:text-slate-700"
                  onClick={() => {
                    setImageDataUrl('');
                    setUseOrderImage(true);
                  }}
                  data-testid="receive-jewellery-clear-image-button"
                >
                  Use order image
                </Button>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              Max 2 MB. Falls back to the order&apos;s image automatically.
            </div>
          </div>
        </div>

        {/* Header fields + Gross Weight */}
        <div className="lg:col-span-7 space-y-3">
          <div>
            <Label htmlFor="gross-weight" className="text-xs text-slate-600">
              Gross Weight (g)
            </Label>
            <Input
              id="gross-weight"
              type="number"
              step="0.001"
              min="0"
              value={grossWeight}
              onChange={(e) => setGrossWeight(e.target.value)}
              placeholder="e.g. 15.000"
              className="h-11 mt-1 font-mono text-lg"
              data-testid="receive-jewellery-gross-weight-input"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              The total weight of the piece as handed over by the goldsmith. We&apos;ll subtract stone grams in Step 2 to derive the Net Weight.
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="jewellery-no" className="text-xs text-slate-600">Jewellery No</Label>
              <Input
                id="jewellery-no"
                value={jewelleryNo}
                onChange={(e) => setJewelleryNo(e.target.value)}
                className="h-9 mt-1 font-mono"
                data-testid="receive-jewellery-no-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Auto-suggested: <span className="font-mono">{preview.suggested_jewellery_no}</span>
              </div>
            </div>
            <div>
              <Label htmlFor="jewellery-title" className="text-xs text-slate-600">Title (optional)</Label>
              <Input
                id="jewellery-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Diamond solitaire ring"
                className="h-9 mt-1"
                data-testid="receive-jewellery-title-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="jewellery-desc" className="text-xs text-slate-600">Description (optional)</Label>
            <Textarea
              id="jewellery-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              data-testid="receive-jewellery-desc-input"
            />
          </div>
        </div>
      </div>
    </Card>
  );

  const renderStepConsumption = () => {
    // Wave 3.22 — Group lines by material hierarchy (Gold → Diamonds →
    // Colour Stones → Pearls) and then by packet (or purity for gold) so
    // the page reads like a stock tally instead of a flat dump.
    //
    // Wave 3.80 — Inside the DIAMOND section, groups are sorted by the
    // canonical sub-category order (Polki → Diamonds → Rose Cuts →
    // Brown Diamonds → Chakri → Other) using the group's
    // ``classification_schema``. Non-diamond materials keep their
    // alphabetical order.
    const MATERIAL_RANK = { gold: 0, diamond: 1, color_stone: 2, pearl: 3 };
    const MATERIAL_HEADING = {
      gold: 'Gold',
      diamond: 'Diamonds',
      color_stone: 'Colour Stones',
      pearl: 'Pearls',
    };
    const groupedSections = (() => {
      const sectionsMap = new Map();
      for (const ln of lines) {
        const mt = ln.material_type;
        if (!sectionsMap.has(mt)) sectionsMap.set(mt, new Map());
        const groupKey = mt === 'gold' ? (ln.purity_id || 'unknown') : (ln.packet_id || 'unknown');
        const label = mt === 'gold'
          ? `${ln.purity_name || ''}${ln.purity_percent ? ` · ${Number(ln.purity_percent).toFixed(2)}%` : ''}`
          : (ln.packet_no || 'Packet');
        const groupsForSection = sectionsMap.get(mt);
        const existing = groupsForSection.get(groupKey) || {
          groupKey, label, lines: [],
          classification_schema: ln.classification_schema || null,
        };
        existing.lines.push(ln);
        groupsForSection.set(groupKey, existing);
      }
      const out = [];
      for (const [mt, groupsMap] of sectionsMap.entries()) {
        let groups = Array.from(groupsMap.values())
          .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true }));
        if (mt === 'diamond') {
          // Wave 3.80 — Enforce canonical Diamond sub-category order.
          groups = sortDiamondItems(
            groups,
            (g) => [
              (g.classification_schema || ''),
              (g.label || ''),
            ].join('|').toLowerCase(),
          );
        }
        out.push({
          material_type: mt,
          heading: MATERIAL_HEADING[mt] || mt,
          icon: MAT_ICON[mt] || Gem,
          groups,
        });
      }
      out.sort((a, b) => (MATERIAL_RANK[a.material_type] ?? 99) - (MATERIAL_RANK[b.material_type] ?? 99));
      return out;
    })();

    // Inline line-row renderer. Closes over `lineState`, `setLineState`,
    // and `goldLossMode` so we don't have to thread them through.
    const renderLineCard = (ln) => {
      const key = `${ln.issue_id}|${ln.line_id}`;
      const s = lineState[key] || {};
      const Icon = MAT_ICON[ln.material_type] || Gem;
      const isGold = ln.material_type === 'gold';
      const update = (patch) => setLineState((prev) => ({
        ...prev,
        [key]: { ...prev[key], ...patch },
      }));
      // Wave 4.x — Ghisai Loss (stones only). Overflow = attributed − used.
      // Ghisai loss is carved out of the overflow as a write-off; the
      // remainder ("net overflow") is what routes to the chosen destination.
      const ovCt = isGold ? 0 : Math.max(0, (ln.attributed_carats || 0) - (+s.consumed_carats || 0));
      const ovPcs = isGold ? 0 : Math.max(0, (ln.attributed_pieces || 0) - (+s.consumed_pieces || 0));
      const glCt = isGold ? 0 : Math.min(Math.max(0, +s.ghisai_loss_carats || 0), ovCt);
      const glPcs = isGold ? 0 : Math.min(Math.max(0, +s.ghisai_loss_pieces || 0), ovPcs);
      const netOvCt = Math.max(0, ovCt - glCt);
      const netOvPcs = Math.max(0, ovPcs - glPcs);
      const hasOverflow = ovCt > 0 || ovPcs > 0;
      return (
        <div
          key={key}
          className="rounded-lg border border-slate-200 bg-white p-3"
          data-testid={`receive-jewellery-line-${key}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon size={16} className="text-slate-500" />
            <div className="font-medium text-sm">{MAT_LABEL[ln.material_type]}</div>
            {isGold ? (
              <Badge variant="outline" className="text-[10px] font-mono">
                {ln.purity_name} · {ln.purity_percent?.toFixed(2)}%
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] font-mono">
                {ln.packet_no} {ln.classification_schema ? `· ${ln.classification_schema}` : ''}
              </Badge>
            )}
            <span className="text-[11px] text-slate-500 ml-auto">Issue {ln.issue_no}</span>
          </div>
          {(ln.shape || ln.size || ln.sieves) ? (
            <div
              className="flex flex-wrap items-center gap-1 mb-2 -mt-1"
              data-testid={`receive-jewellery-line-particulars-${key}`}
            >
              {ln.shape ? <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">Shape: {ln.shape}</span> : null}
              {ln.size ? <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">Size: {ln.size}</span> : null}
              {ln.sieves ? <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">Sieves: {ln.sieves}</span> : null}
            </div>
          ) : null}
          {ln.note ? (
            <div
              className="flex items-start gap-1 mb-2 -mt-1 text-[11px] text-slate-600 italic bg-amber-50/70 border border-amber-100 rounded px-2 py-1"
              title={ln.note}
              data-testid={`receive-jewellery-line-note-${key}`}
            >
              <StickyNote size={12} className="mt-[1px] shrink-0 text-amber-500" />
              <span><span className="font-medium not-italic text-slate-500">Issue note:</span> {ln.note}</span>
            </div>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            {isGold ? (
              <>
                <div className="md:col-span-3">
                  <Label className="text-[11px] text-slate-600">Attributed (g · source purity)</Label>
                  <div className="font-mono text-sm py-1.5">{formatGrams(ln.attributed_grams)} g</div>
                </div>
                <div className="md:col-span-3">
                  {/* Wave 3.20 — Gold consumed is auto-derived from Gross Weight
                      minus stones (Net) and the gold-loss settlement chosen in
                      Step 3. The user no longer enters this directly. */}
                  <Label className="text-[11px] text-slate-600">Auto-consumed (g)</Label>
                  <div
                    className="font-mono text-sm py-1.5 text-emerald-700"
                    data-testid={`receive-jewellery-consumed-grams-${key}`}
                  >
                    {formatGrams(+s.consumed_grams || 0)} g
                  </div>
                  <div className="text-[10px] text-slate-500">
                    = {derived.mounting_grams > 0 ? 'Net − Mountings' : 'Net Weight'} {goldLossMode === 'gold_grams' ? '+ Gold Loss' : ''} (system computed)
                  </div>
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[11px] text-slate-600">Overflow (g)</Label>
                  <div className="font-mono text-sm py-1.5">
                    {formatGrams(Math.max(0, (ln.attributed_grams || 0) - (+s.consumed_grams || 0)))} g
                  </div>
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[11px] text-slate-600">Overflow destination</Label>
                  <div className="text-[11px] py-1.5 text-emerald-700">→ Goldsmith Unattributed (999)</div>
                </div>
              </>
            ) : (
              <>
                <div className="md:col-span-2">
                  <Label className="text-[11px] text-slate-600">Attributed</Label>
                  <div className="font-mono text-sm py-1.5">
                    {formatCarats(ln.attributed_carats)} ct · {ln.attributed_pieces} pcs
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[11px] text-slate-600" htmlFor={`ct-${key}`}>Used (ct)</Label>
                  <Input
                    id={`ct-${key}`}
                    type="number"
                    step="0.01"
                    min="0"
                    max={ln.attributed_carats}
                    value={s.consumed_carats ?? ''}
                    onChange={(e) => update({ consumed_carats: e.target.value })}
                    className="h-9"
                        data-testid={`receive-jewellery-consumed-carats-${key}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[11px] text-slate-600" htmlFor={`pcs-${key}`}>Used (pcs)</Label>
                      <Input
                        id={`pcs-${key}`}
                        type="number"
                        step="1"
                        min="0"
                        max={ln.attributed_pieces}
                        value={s.consumed_pieces ?? ''}
                        onChange={(e) => update({ consumed_pieces: e.target.value })}
                        className="h-9"
                        data-testid={`receive-jewellery-consumed-pieces-${key}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[11px] text-slate-600">Overflow (total)</Label>
                      <div className="font-mono text-sm py-1.5">
                        {formatCarats(ovCt)} ct · {ovPcs} pcs
                      </div>
                    </div>
                    {hasOverflow ? (
                      <>
                        <div className="md:col-span-2">
                          <Label className="text-[11px] text-slate-600" htmlFor={`ghisai-ct-${key}`}>Ghisai Loss (ct)</Label>
                          <Input
                            id={`ghisai-ct-${key}`}
                            type="number"
                            step="0.01"
                            min="0"
                            max={ovCt}
                            placeholder="0"
                            value={s.ghisai_loss_carats ?? ''}
                            onChange={(e) => update({ ghisai_loss_carats: e.target.value })}
                            className="h-9"
                            data-testid={`receive-jewellery-ghisai-carats-${key}`}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-[11px] text-slate-600" htmlFor={`ghisai-pcs-${key}`}>Ghisai Loss (pcs)</Label>
                          <Input
                            id={`ghisai-pcs-${key}`}
                            type="number"
                            step="1"
                            min="0"
                            max={ovPcs}
                            placeholder="0"
                            value={s.ghisai_loss_pieces ?? ''}
                            onChange={(e) => update({ ghisai_loss_pieces: e.target.value })}
                            className="h-9"
                            data-testid={`receive-jewellery-ghisai-pieces-${key}`}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-[11px] text-slate-600">Net overflow</Label>
                          <div
                            className="font-mono text-sm py-1.5 text-slate-700"
                            data-testid={`receive-jewellery-net-overflow-${key}`}
                          >
                            {formatCarats(netOvCt)} ct · {netOvPcs} pcs
                          </div>
                        </div>
                        <div className="md:col-span-4">
                          <Label className="text-[11px] text-slate-600">Net overflow destination</Label>
                          <Select
                            value={s.overflow_destination || 'unattributed_goldsmith'}
                            onValueChange={(v) => update({ overflow_destination: v })}
                            disabled={netOvCt <= 0 && netOvPcs <= 0}
                          >
                            <SelectTrigger className="h-9" data-testid={`receive-jewellery-overflow-dest-${key}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unattributed_goldsmith">Send to Goldsmith Unattributed</SelectItem>
                              <SelectItem value="return_to_packet">Return to Original Packet ({ln.packet_no})</SelectItem>
                            </SelectContent>
                          </Select>
                          {(glCt > 0 || glPcs > 0) && (
                            <div
                              className="text-[10px] text-rose-600 mt-1"
                              data-testid={`receive-jewellery-ghisai-note-${key}`}
                            >
                              Ghisai Loss {formatCarats(glCt)} ct · {glPcs} pcs written off (not returned to packet or pool)
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="md:col-span-6 flex items-end">
                        <span className="text-[11px] text-slate-400 py-1.5">No overflow — all attributed material consumed.</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
    };

    // Wave 3.99ac — Mountings (semi-finished gold pieces) issued into this
    // order. Read-only: mountings ARE gold and become part of the finished
    // piece, but the karigar does not craft them, so they carry no
    // consumption input. Their weight is excluded from labour + gold loss
    // in Step 3. Rendered directly under the Gold section per spec.
    const goldSectionExists = groupedSections.some((s) => s.material_type === 'gold');
    const renderMountingsBlock = () => {
      if ((preview?.mountings || []).length === 0) return null;
      return (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50/40"
          data-testid="receive-jewellery-section-mountings"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-200 bg-white rounded-t-lg">
            <Wrench size={15} className="text-amber-700" />
            <div className="font-semibold text-sm text-[var(--ink-navy)]">Mountings (Gold)</div>
            <Badge variant="outline" className="font-mono text-[10px] ml-1">
              {preview.mountings.length} mounting{preview.mountings.length === 1 ? '' : 's'} · {formatGrams(derived.mounting_grams)} g
            </Badge>
          </div>
          <div className="p-3 space-y-2">
            {preview.mountings.map((m) => (
              <div
                key={m.mounting_id}
                className="rounded-md border border-amber-200 bg-white p-3 flex flex-wrap items-center gap-3"
                data-testid={`receive-jewellery-mounting-${m.mounting_id}`}
              >
                <Wrench size={14} className="text-amber-600" />
                <div className="font-medium text-sm text-[var(--ink-navy)]">{m.mounting_name}</div>
                {(m.purity_name || m.purity_karat) && (
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {m.purity_name || m.purity_karat}
                    {m.purity_percent ? ` · ${Number(m.purity_percent).toFixed(2)}%` : ''}
                  </Badge>
                )}
                <div className="ml-auto font-mono text-sm text-amber-800">{formatGrams(m.grams)} g</div>
              </div>
            ))}
            <div className="text-[11px] text-amber-700 bg-amber-100/60 rounded px-2 py-1.5">
              Mountings are ready-made gold — they add to the piece&apos;s net weight but the karigar earns
              no labour on them. Their weight is deducted from the Net Weight before labour &amp; gold loss
              are calculated in Step 3.
            </div>
          </div>
        </div>
      );
    };

    return (
      <Card className="p-5" data-testid="receive-jewellery-step-consumption">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-medium text-[var(--ink-navy)]">Step 2 · Raw Material Used</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Enter stones/diamonds used. <b>Gold consumed is auto-derived</b> from Gross Weight minus stones (Net) + any gold loss declared in Step 3. Unused stone remainder is routed to either the goldsmith&apos;s <span className="font-medium">unattributed pool</span> or back to the <span className="font-medium">original packet</span> (your choice per line). If the goldsmith recut a stone and some weight was ground away and not returned, record it as <span className="font-medium">Ghisai Loss</span> — it is written off (not returned to the packet or pool) and the remaining overflow routes to your chosen destination.
            </div>
          </div>
          <Badge variant="outline" className="font-mono text-[11px]">
            {lines.length} lines
          </Badge>
        </div>
        {/* Wave 3.22 — Sections follow the canonical material hierarchy
            (Gold → Diamonds → Colour Stones → Pearls). Within each section
            lines are grouped by packet (carat-based materials) or purity
            (gold) so the user can see and process all entries from one
            packet together. */}
        <div className="space-y-5">
          {groupedSections.map((section) => {
            const SectionIcon = section.icon || Gem;
            const sectionTotalLines = section.groups.reduce((acc, g) => acc + g.lines.length, 0);
            return (
              <React.Fragment key={section.material_type}>
              <div
                className="rounded-lg border border-[var(--divider)] bg-slate-50/40"
                data-testid={`receive-jewellery-section-${section.material_type}`}
              >
                <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--divider)] bg-white rounded-t-lg">
                  <SectionIcon size={15} className="text-[var(--ink-navy)]" />
                  <div className="font-semibold text-sm text-[var(--ink-navy)]">{section.heading}</div>
                  <Badge variant="outline" className="font-mono text-[10px] ml-1">
                    {section.groups.length} {section.material_type === 'gold' ? 'purit' : 'packet'}{section.groups.length === 1 ? (section.material_type === 'gold' ? 'y' : '') : (section.material_type === 'gold' ? 'ies' : 's')} · {sectionTotalLines} line{sectionTotalLines === 1 ? '' : 's'}
                  </Badge>
                </div>
                <div className="p-3 space-y-3">
                  {section.groups.map((g) => (
                    <div
                      key={g.groupKey}
                      className="rounded-md border border-slate-200 bg-white/70"
                      data-testid={`receive-jewellery-group-${section.material_type}-${g.groupKey}`}
                    >
                      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--divider)] bg-white">
                        <Badge className="font-mono text-[10px] bg-[var(--ink-navy)]/5 text-[var(--ink-navy)] hover:bg-[var(--ink-navy)]/10 border border-[var(--ink-navy)]/10">
                          {g.label}
                        </Badge>
                        {g.classification_schema && section.material_type !== 'gold' && (
                          <span className="text-[11px] text-slate-500">{g.classification_schema}</span>
                        )}
                        <span className="text-[11px] text-slate-500 ml-auto">
                          {g.lines.length} line{g.lines.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="p-2 space-y-2">
                        {g.lines.map((ln) => renderLineCard(ln))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Mountings render directly under the Gold section. */}
              {section.material_type === 'gold' && renderMountingsBlock()}
              </React.Fragment>
            );
          })}
          {/* Fallback: if the order has mountings but no gold attribution
              lines, still surface the Mountings block. */}
          {!goldSectionExists && renderMountingsBlock()}
        </div>

      {/* Live stone & gold totals strip */}
      <Separator className="my-4" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {derived.d_ct > 0 || derived.d_pcs > 0 ? (
          <ReadOnlyStat
            label="Diamonds used"
            value={`${formatCarats(derived.d_ct)} ct · ${derived.d_pcs} pcs`}
            icon={<Gem size={12} />}
          />
        ) : null}
        {derived.cs_ct > 0 || derived.cs_pcs > 0 ? (
          <ReadOnlyStat
            label="Colour Stones used"
            value={`${formatCarats(derived.cs_ct)} ct · ${derived.cs_pcs} pcs`}
            icon={<Sparkles size={12} />}
          />
        ) : null}
        {derived.p_ct > 0 || derived.p_pcs > 0 ? (
          <ReadOnlyStat
            label="Pearls used"
            value={`${formatCarats(derived.p_ct)} ct · ${derived.p_pcs} pcs`}
            icon={<Circle size={12} />}
          />
        ) : null}
        {derived.mounting_grams > 0 ? (
          <ReadOnlyStat
            label="Mountings (gold)"
            value={`${formatGrams(derived.mounting_grams)} g`}
            icon={<Wrench size={12} />}
          />
        ) : null}
        <ReadOnlyStat
          label="Stones → g (÷5)"
          value={`${formatGrams(derived.stone_grams_eq)} g`}
          icon={<Calculator size={12} />}
        />
      </div>
    </Card>
    );
  };
  const renderStepLabour = () => (
    <Card className="p-5" data-testid="receive-jewellery-step-labour">
      <div className="font-medium text-[var(--ink-navy)] mb-1">Step 3 · Labour &amp; Gold Loss</div>
      <div className="text-xs text-slate-600 mb-4">
        Net Weight = Gross − (Σ stone carats ÷ 5). Labour &amp; Gold Loss are charged on the
        <b> labour-eligible weight = Net − Mountings</b> (the karigar earns nothing on ready-made mountings). Gold Loss is optional.
      </div>

      {/* Net weight strip */}
      <div className={`grid gap-3 mb-4 ${derived.mounting_grams > 0 ? 'md:grid-cols-3 lg:grid-cols-5' : 'md:grid-cols-3'}`}>
        <ReadOnlyStat label="Gross" value={`${formatGrams(derived.gross)} g`} mono />
        <ReadOnlyStat label="− Stones (÷5)" value={`${formatGrams(derived.stone_grams_eq)} g`} mono />
        <ReadOnlyStat
          label={`Net Weight (${preview.order.gold_purity_name || 'order purity'})`}
          value={`${formatGrams(derived.net)} g`}
          mono
          highlight
        />
        {derived.mounting_grams > 0 && (
          <>
            <ReadOnlyStat
              label="− Mountings"
              value={`${formatGrams(derived.mounting_grams)} g`}
              mono
              icon={<Wrench size={12} />}
            />
            <ReadOnlyStat
              label="Labour-eligible weight"
              value={`${formatGrams(derived.labour_eligible_net)} g`}
              mono
              highlight
            />
          </>
        )}
      </div>
      {derived.net < 0 && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mb-4" data-testid="receive-jewellery-net-error">
          Net Weight cannot be negative. Recheck stones vs gross weight.
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="labour-rate" className="text-xs text-slate-600">Labour Rate (₹/g)</Label>
          <Input
            id="labour-rate"
            type="number"
            step="0.01"
            min="0"
            value={labourRate}
            onChange={(e) => setLabourRate(e.target.value)}
            placeholder="e.g. 800"
            className="h-10 mt-1 font-mono"
            data-testid="receive-jewellery-labour-rate-input"
          />
        </div>
        <ReadOnlyStat
          label={derived.mounting_grams > 0 ? '(Net − Mountings) × Rate = Labour' : 'Net × Rate = Labour'}
          value={formatINR(derived.labour_amt)}
          mono
          highlight
        />
        <div>
          <Label htmlFor="loss-pct" className="text-xs text-slate-600">Gold Loss %</Label>
          <Input
            id="loss-pct"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={goldLossPercent}
            onChange={(e) => setGoldLossPercent(e.target.value)}
            placeholder="0 = no loss"
            className="h-10 mt-1 font-mono"
            data-testid="receive-jewellery-loss-percent-input"
          />
        </div>
      </div>

      {/* Wave 3.36 — Credit Days for the labour bill */}
      <div className="mt-4 grid md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="credit-days" className="text-xs text-slate-600">
            Credit Days <span className="text-slate-400 text-[10px]">(optional)</span>
          </Label>
          <Input
            id="credit-days"
            type="number"
            min="0"
            max="365"
            value={creditDays}
            onChange={(e) => setCreditDays(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="e.g. 15 — leave blank for none"
            className="h-10 mt-1 font-mono"
            data-testid="receive-jewellery-credit-days-input"
          />
        </div>
        <div className="md:col-span-2 self-end text-[11px] text-slate-500">
          {creditDays && Number(creditDays) > 0 ? (
            <span data-testid="receive-jewellery-due-date-preview">
              Labour due date ≈ <strong>
                {(() => {
                  const d = new Date();
                  d.setDate(d.getDate() + Number(creditDays));
                  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                })()}
              </strong>. Payment reminders fire at T-7, T-1 and on the due date.
            </span>
          ) : (
            <span className="text-slate-400">No credit period — labour bill won't have a due date or reminders.</span>
          )}
        </div>
      </div>

      {derived.loss_pct > 0 && (
        <div className="mt-4">
          <Label className="text-xs text-slate-600">Gold Loss settlement mode</Label>
          <RadioGroup
            value={goldLossMode}
            onValueChange={setGoldLossMode}
            className="grid md:grid-cols-2 gap-3 mt-2"
          >
            <label className={`rounded-lg border p-3 cursor-pointer transition-colors ${goldLossMode === 'gold_grams' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="gold_grams" id="loss-mode-grams" data-testid="receive-jewellery-loss-mode-grams" />
                <div>
                  <div className="text-sm font-medium">Gold Loss adjusted from Gold with Goldsmith</div>
                  <div className="text-[11px] text-slate-600">
                    {formatGrams(derived.loss_grams)} g (order purity) is debited from the goldsmith's gold pool on top of the labour-eligible weight. No cash component on the bill.
                  </div>
                </div>
              </div>
            </label>
            <label className={`rounded-lg border p-3 cursor-pointer transition-colors ${goldLossMode === 'rupees' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="rupees" id="loss-mode-rupees" data-testid="receive-jewellery-loss-mode-rupees" />
                <div>
                  <div className="text-sm font-medium">Goldsmith bills loss in cash</div>
                  <div className="text-[11px] text-slate-600">
                    {formatGrams(derived.loss_g999)} g 999 × gold rate is added to the goldsmith's bill. No extra physical gold deducted.
                  </div>
                </div>
              </div>
            </label>
          </RadioGroup>
          {/* Wave 3.20 — Gold 999 rate is required regardless of mode so the
              product-cost view always reflects the value of lost gold.
              Wave 3.20.1 — Empty rate auto-falls back server-side to the latest
              running-average gold rate. */}
          <div className="mt-3 grid md:grid-cols-3 gap-3 items-end">
            <div>
              <Label htmlFor="rate-999" className="text-xs text-slate-600">Gold 999 rate (₹/g)</Label>
              <Input
                id="rate-999"
                type="number"
                step="0.01"
                min="0"
                value={goldRate999}
                onChange={(e) => setGoldRate999(e.target.value)}
                placeholder={preview?.gold_rate_999_snapshot ? String(preview.gold_rate_999_snapshot) : 'latest system rate'}
                className="h-10 mt-1 font-mono"
                data-testid="receive-jewellery-rate-999-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Pre-filled from the latest system rate. If left blank, the server uses the same fallback automatically.
              </div>
            </div>
            <ReadOnlyStat label="Gold Loss Value (₹)" value={formatINR(derived.loss_value_inr)} mono highlight />
            <ReadOnlyStat
              label={goldLossMode === 'rupees' ? 'Goldsmith Bill (₹)' : 'Product Labour Cost (₹)'}
              value={formatINR(goldLossMode === 'rupees' ? derived.total_amt : derived.total_product_labour_amt)}
              mono
              highlight
            />
          </div>
          {goldLossMode === 'gold_grams' && (
            <div className="mt-2 text-[11px] text-slate-600">
              Goldsmith bill = {formatINR(derived.labour_amt)} (labour only, no cash for loss).
              Product cost = {formatINR(derived.total_product_labour_amt)} (labour + value of lost gold).
            </div>
          )}
        </div>
      )}

      {/* Shortage panel */}
      {derived.shortage > 0 && (
        <ShortagePanel
          shortage={derived.shortage}
          orderPurityName={preview.order.gold_purity_name}
          orderPurityPercent={preview.order.gold_purity_percent}
          goldsmithId={preview.order.goldsmith_id}
          goldsmithName={preview.order.goldsmith_name}
          orderId={preview.order.id}
          orderPurityId={preview.order.gold_purity_id}
          companyId={effectiveCompanyId}
          onResolved={() => fetchPreview({ preserveState: true })}
          open={shortageDialogOpen}
          onOpenChange={setShortageDialogOpen}
        />
      )}

      {derived.submission_mismatch && derived.shortage <= 0 && (
        <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="receive-jewellery-gold-mismatch">
          System couldn't fully allocate gold — {formatGrams(derived.gold_src_in_order_purity)} g (in order purity) allocated against {formatGrams(derived.expected_gold_draw)} g needed (Net{goldLossMode === 'gold_grams' ? ' + Loss' : ''}). Likely a configuration issue.
        </div>
      )}
    </Card>
  );

  const renderStepPreview = () => (
    <Card className="p-5" data-testid="receive-jewellery-step-preview">
      <div className="font-medium text-[var(--ink-navy)] mb-3">Step 4 · Preview & Confirm</div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-slate-500">Header</div>
          <ReviewRow label="Jewellery No" value={jewelleryNo || preview.suggested_jewellery_no} />
          <ReviewRow label="Title" value={title || '—'} />
          <ReviewRow label="Order" value={preview.order.order_no} />
          <ReviewRow label="Goldsmith" value={preview.order.goldsmith_name} />
          <ReviewRow
            label="Order purity"
            value={`${preview.order.gold_purity_name || ''} (${(preview.order.gold_purity_percent || 0).toFixed(2)}%)`}
          />
        </div>
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-slate-500">Weights</div>
          <ReviewRow label="Gross" value={`${formatGrams(derived.gross)} g`} mono />
          <ReviewRow label="Stones (Σ / 5)" value={`${formatGrams(derived.stone_grams_eq)} g`} mono />
          <ReviewRow label="Net Weight" value={`${formatGrams(derived.net)} g`} mono highlight />
          {derived.mounting_grams > 0 && (
            <>
              <ReviewRow label="− Mountings (gold)" value={`${formatGrams(derived.mounting_grams)} g`} mono />
              <ReviewRow label="Labour-eligible weight" value={`${formatGrams(derived.labour_eligible_net)} g`} mono highlight />
            </>
          )}
          {derived.loss_pct > 0 && goldLossMode === 'gold_grams' && (
            <ReviewRow
              label={`+ Gold Loss (${derived.loss_pct}%)`}
              value={`${formatGrams(derived.loss_grams)} g`}
              mono
            />
          )}
        </div>
      </div>

      <Separator className="my-4" />

      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Consumption breakdown</div>
      <div className="space-y-1.5">
        {(() => {
          const renderRow = (ln) => {
            const key = `${ln.issue_id}|${ln.line_id}`;
            const s = lineState[key] || {};
            const isGold = ln.material_type === 'gold';
            const overflow_ct = !isGold ? Math.max(0, (ln.attributed_carats || 0) - (+s.consumed_carats || 0)) : 0;
            const overflow_pcs = !isGold ? Math.max(0, (ln.attributed_pieces || 0) - (+s.consumed_pieces || 0)) : 0;
            const overflow_g = isGold ? Math.max(0, (ln.attributed_grams || 0) - (+s.consumed_grams || 0)) : 0;
            const ghisai_ct = !isGold ? Math.min(Math.max(0, +s.ghisai_loss_carats || 0), overflow_ct) : 0;
            const ghisai_pcs = !isGold ? Math.min(Math.max(0, +s.ghisai_loss_pieces || 0), overflow_pcs) : 0;
            const net_ct = Math.max(0, overflow_ct - ghisai_ct);
            const net_pcs = Math.max(0, overflow_pcs - ghisai_pcs);
            const hasGhisai = ghisai_ct > 0 || ghisai_pcs > 0;
            return (
              <div key={key} className="text-xs flex flex-wrap items-center gap-x-3 gap-y-1 py-1 border-b border-slate-100 last:border-b-0">
                <Badge variant="outline" className="font-mono text-[10px]">
                  {MAT_LABEL[ln.material_type]}
                </Badge>
                <span className="font-mono">{isGold ? ln.purity_name : ln.packet_no}</span>
                <span className="text-slate-500">Used:</span>
                <span className="font-mono">
                  {isGold
                    ? `${formatGrams(+s.consumed_grams || 0)} g`
                    : `${formatCarats(+s.consumed_carats || 0)} ct · ${+s.consumed_pieces || 0} pcs`}
                </span>
                {(overflow_g > 0 || overflow_ct > 0 || overflow_pcs > 0) && (
                  <>
                    <span className="text-slate-500">Overflow:</span>
                    <span className="font-mono">
                      {isGold
                        ? `${formatGrams(overflow_g)} g`
                        : `${formatCarats(overflow_ct)} ct · ${overflow_pcs} pcs`}
                    </span>
                    {hasGhisai && (
                      <span className="text-rose-600 text-[11px] font-mono" data-testid={`receive-jewellery-review-ghisai-${key}`}>
                        ✂ Ghisai −{formatCarats(ghisai_ct)} ct · {ghisai_pcs} pcs (write-off)
                      </span>
                    )}
                    {(!isGold && (net_ct > 0 || net_pcs > 0)) || isGold ? (
                      <span className="text-emerald-700 text-[11px]">
                        {isGold
                          ? '→ Goldsmith Unattributed (999)'
                          : `${hasGhisai ? `Net ${formatCarats(net_ct)} ct · ${net_pcs} pcs ` : ''}→ ${s.overflow_destination === 'return_to_packet' ? `Back to ${ln.packet_no}` : 'Goldsmith Unattributed'}`}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            );
          };
          const goldLines = lines.filter((ln) => ln.material_type === 'gold');
          const nonGoldLines = lines.filter((ln) => ln.material_type !== 'gold');
          // Wave 3.99ac — Mountings (ready-made gold, no overflow, no labour)
          // are listed directly under the Gold lines and above Diamonds.
          const mountingRows = (preview?.mountings || []).map((m) => (
            <div
              key={`mnt-${m.mounting_id}`}
              className="text-xs flex flex-wrap items-center gap-x-3 gap-y-1 py-1 border-b border-slate-100 last:border-b-0"
              data-testid={`receive-jewellery-preview-mounting-${m.mounting_id}`}
            >
              <Badge variant="outline" className="font-mono text-[10px] border-amber-300 text-amber-700">
                Mounting
              </Badge>
              <span className="font-mono">{m.mounting_name}</span>
              {(m.purity_name || m.purity_karat) && (
                <span className="text-slate-500 font-mono text-[11px]">{m.purity_name || m.purity_karat}</span>
              )}
              <span className="text-slate-500">Used:</span>
              <span className="font-mono">{formatGrams(m.grams)} g</span>
              <span className="text-amber-700 text-[11px]">· no labour (ready-made gold)</span>
            </div>
          ));
          return (
            <>
              {goldLines.map(renderRow)}
              {mountingRows}
              {nonGoldLines.map(renderRow)}
            </>
          );
        })()}
      </div>

      <Separator className="my-4" />

      <div className="grid md:grid-cols-4 gap-4">
        <ReviewRow label={derived.mounting_grams > 0 ? 'Labour ((Net−Mountings) × Rate)' : 'Labour (Net × Rate)'} value={formatINR(derived.labour_amt)} mono highlight />
        {derived.loss_pct > 0 && (
          <ReviewRow label="Gold Loss Value (₹)" value={formatINR(derived.loss_value_inr)} mono highlight />
        )}
        <ReviewRow
          label="Goldsmith Bill"
          value={formatINR(derived.total_amt)}
          mono
          highlight
        />
        {derived.loss_pct > 0 && goldLossMode === 'gold_grams' && (
          <ReviewRow
            label="Product Labour Cost"
            value={formatINR(derived.total_product_labour_amt)}
            mono
            highlight
          />
        )}
      </div>
      {derived.loss_pct > 0 && goldLossMode === 'gold_grams' && (
        <div className="mt-2 text-[11px] text-slate-600 italic">
          Goldsmith receives <b>{formatINR(derived.total_amt)}</b> (labour only — extra gold is recovered physically). The product's true labour cost is <b>{formatINR(derived.total_product_labour_amt)}</b> including the value of the lost gold.
        </div>
      )}

      <div className="mt-5 text-xs text-slate-500">
        On confirm: consumed RM is debited from the goldsmith, overflow is routed per your choices, the jewellery record is persisted, and (if all attribution is exhausted) the order auto-completes.
      </div>
    </Card>
  );

  const stepView = [
    renderStepCapture,
    renderStepConsumption,
    renderStepLabour,
    renderStepPreview,
  ][stepIdx]();

  return (
    <div className="space-y-4" data-testid="receive-jewellery-wizard">
      {/* Header summary card */}
      <Card className="p-4 bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700">Receiving against</div>
            <div className="font-mono text-lg text-[var(--ink-navy)]">{preview.order.order_no}</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Goldsmith: <span className="font-medium">{preview.order.goldsmith_name}</span>
              {preview.order.gold_purity_name
                ? ` · Purity: ${preview.order.gold_purity_name} (${preview.order.gold_purity_percent.toFixed(2)}%)`
                : ''}
            </div>
          </div>
          <div className="text-xs text-slate-500">
            Auto-suggested no: <span className="font-mono text-emerald-700">{preview.suggested_jewellery_no}</span>
          </div>
        </div>
      </Card>

      {/* Stepper */}
      <Card className="p-3" data-testid="receive-jewellery-stepper">
        <div className="flex items-center justify-between text-xs">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1 flex items-center">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium ${
                  i === stepIdx ? 'bg-emerald-600 text-white'
                    : i < stepIdx ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-slate-100 text-slate-500'
                }`}>
                  {i < stepIdx ? <Check size={12} /> : i + 1}
                </div>
                <span className={i === stepIdx ? 'font-medium text-[var(--ink-navy)]' : 'text-slate-500'}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-3" />}
            </div>
          ))}
        </div>
      </Card>

      {stepView}

      <div className="flex items-center justify-between" data-testid="receive-jewellery-action-bar">
        <Button
          variant="outline"
          onClick={() => stepIdx === 0 ? navigate(-1) : setStepIdx(stepIdx - 1)}
          disabled={submitting}
          data-testid="receive-jewellery-prev-button"
        >
          <ArrowLeft size={14} className="mr-1" /> {stepIdx === 0 ? 'Cancel' : 'Back'}
        </Button>
        {stepIdx < STEPS.length - 1 ? (
          <Button
            className="luxury-button"
            onClick={() => setStepIdx(stepIdx + 1)}
            disabled={!stepValid[stepIdx]}
            data-testid="receive-jewellery-next-button"
          >
            Next <ArrowRight size={14} className="ml-1" />
          </Button>
        ) : (
          <Button
            className="luxury-button"
            onClick={handleSubmit}
            disabled={submitting || !stepValid[0] || !stepValid[1] || !stepValid[2]}
            data-testid="receive-jewellery-confirm-button"
          >
            {submitting ? 'Saving…' : (<>Confirm &amp; Receive <Check size={14} className="ml-1" /></>)}
          </Button>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// ShortagePanel — inline banner + resolver dialog
// ===========================================================================
function ShortagePanel({
  shortage, orderPurityName, orderPurityPercent,
  goldsmithId, goldsmithName, orderId, orderPurityId, companyId,
  onResolved, open, onOpenChange,
}) {
  return (
    <>
      <div
        className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4"
        data-testid="receive-jewellery-shortage-panel"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-700 mt-0.5" size={18} />
          <div className="flex-1">
            <div className="font-medium text-amber-900 text-sm">
              Gold shortage — {formatGrams(shortage)} g short in {orderPurityName || 'order purity'}
            </div>
            <div className="text-xs text-amber-800 mt-1 leading-relaxed">
              The goldsmith doesn&apos;t have enough gold attributed to this order to cover the required Net Weight
              {orderPurityPercent ? ` (purity ${orderPurityPercent.toFixed(2)}%)` : ''}.
              You must either move gold from their unattributed pool or issue fresh gold from stock before you can receive the jewellery.
            </div>
            <Button
              size="sm"
              className="luxury-button mt-3 h-8"
              onClick={() => onOpenChange(true)}
              data-testid="receive-jewellery-shortage-resolve-button"
            >
              Resolve shortage
            </Button>
          </div>
        </div>
      </div>

      <ShortageDialog
        open={open}
        onOpenChange={onOpenChange}
        shortage={shortage}
        orderId={orderId}
        orderPurityId={orderPurityId}
        orderPurityPercent={orderPurityPercent}
        orderPurityName={orderPurityName}
        goldsmithId={goldsmithId}
        goldsmithName={goldsmithName}
        companyId={companyId}
        onResolved={() => { onOpenChange(false); onResolved(); }}
      />
    </>
  );
}

function ShortageDialog({
  open, onOpenChange, shortage,
  orderId, orderPurityId, orderPurityPercent, orderPurityName,
  goldsmithId, goldsmithName, companyId,
  onResolved,
}) {
  const [pool, setPool] = useState(null);
  const [tab, setTab] = useState('unattributed');  // 'unattributed' | 'fresh_issue'
  const [busy, setBusy] = useState(false);

  // Resolver A — pick an unattributed gold line + grams (in source purity).
  const [pickedLineKey, setPickedLineKey] = useState('');
  const [moveGrams, setMoveGrams] = useState('');

  // Resolver B — fresh issue from stock.
  const [issuePurityId, setIssuePurityId] = useState('');
  const [issueGrams, setIssueGrams] = useState('');
  const [issueRate, setIssueRate] = useState('');

  useEffect(() => {
    if (!open || !goldsmithId || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/jewellery/gold-pool/${goldsmithId}`, {
          params: { company_id: companyId },
        });
        if (cancelled) return;
        setPool(res.data);
        // Seed defaults
        const firstLine = (res.data.unattributed_lines || [])[0];
        if (firstLine) {
          setPickedLineKey(`${firstLine.issue_id}|${firstLine.line_id}`);
        }
        const defaultPurity = orderPurityId || (res.data.purities || [])[0]?.id;
        setIssuePurityId(defaultPurity || '');
        setIssueRate(String(res.data.rate_per_g_999_snapshot || ''));
        // Pre-fill the shortage amount in source-purity terms for the unattributed picker.
        if (firstLine?.purity_percent && orderPurityPercent) {
          const grams_source = +(shortage * orderPurityPercent / firstLine.purity_percent).toFixed(3);
          setMoveGrams(String(grams_source));
        }
        // Fresh-issue: pre-fill shortage in order purity (assume target purity = order purity).
        setIssueGrams(String(shortage));
      } catch (e) {
        toast.error(extractError(e, 'Failed to load gold pool'));
      }
    })();
    return () => { cancelled = true; };
  }, [open, goldsmithId, companyId, shortage, orderPurityId, orderPurityPercent]);

  const pickedLine = useMemo(() => {
    if (!pool || !pickedLineKey) return null;
    return (pool.unattributed_lines || []).find(
      (ln) => `${ln.issue_id}|${ln.line_id}` === pickedLineKey,
    ) || null;
  }, [pool, pickedLineKey]);

  // When user picks a different unattributed line, re-derive the auto-filled grams.
  useEffect(() => {
    if (!pickedLine || !orderPurityPercent || !pickedLine.purity_percent) return;
    const grams_source = +(shortage * orderPurityPercent / pickedLine.purity_percent).toFixed(3);
    setMoveGrams(String(Math.min(grams_source, pickedLine.unattributed_grams || 0)));
  }, [pickedLineKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMoveFromUnattributed = async () => {
    if (!pickedLine) {
      toast.error('Select an unattributed line to move from');
      return;
    }
    const g = +moveGrams || 0;
    if (g <= 0) {
      toast.error('Grams to move must be > 0');
      return;
    }
    if (g > (pickedLine.unattributed_grams || 0) + 1e-6) {
      toast.error('Cannot move more than available unattributed grams');
      return;
    }
    setBusy(true);
    try {
      await api.post('/goldsmith-issues/attribute', {
        issue_id: pickedLine.issue_id,
        line_id: pickedLine.line_id,
        order_id: orderId,
        grams: g,
        note: 'Moved to cover gold shortage during jewellery receipt',
      });
      toast.success(`Moved ${formatGrams(g)} g (${pickedLine.purity_name}) to this order`);
      onResolved();
    } catch (e) {
      toast.error(extractError(e, 'Failed to attribute unattributed gold'));
    } finally {
      setBusy(false);
    }
  };

  const handleFreshIssue = async () => {
    if (!issuePurityId) {
      toast.error('Pick the purity to issue');
      return;
    }
    const g = +issueGrams || 0;
    if (g <= 0) {
      toast.error('Grams to issue must be > 0');
      return;
    }
    const r = +issueRate || 0;
    if (r <= 0) {
      toast.error('Provide a positive ₹/g (999) rate for the new stock issue');
      return;
    }
    setBusy(true);
    try {
      await api.post('/goldsmith-issues/', {
        goldsmith_id: goldsmithId,
        company_id: companyId,
        reference: 'Gold shortage cover',
        note: 'Fresh gold issued from stock to cover shortage during jewellery receipt',
        lines: [{
          material_type: 'gold',
          purity_id: issuePurityId,
          grams: g,
          rate_per_g_999: r,
          order_id: orderId,
          note: 'Shortage cover',
        }],
      });
      toast.success(`Issued ${formatGrams(g)} g from stock and attributed to this order`);
      onResolved();
    } catch (e) {
      toast.error(extractError(e, 'Failed to issue fresh gold'));
    } finally {
      setBusy(false);
    }
  };

  const unattributedLines = pool?.unattributed_lines || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="receive-jewellery-shortage-dialog">
        <DialogHeader>
          <DialogTitle>Resolve Gold Shortage</DialogTitle>
          <DialogDescription>
            You need {formatGrams(shortage)} g of additional gold (in {orderPurityName} purity) attributed to this order. Choose a resolution path below.
          </DialogDescription>
        </DialogHeader>

        {/* Tab selector */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setTab('unattributed')}
            className={`rounded-md border px-3 py-2 text-left transition-colors ${tab === 'unattributed' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
            data-testid="receive-jewellery-shortage-tab-unattributed"
          >
            <div className="flex items-center gap-2 text-sm font-medium">
              <Wrench size={14} className="text-emerald-700" />
              Use Unattributed Gold
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">
              Move gold from {goldsmithName}&apos;s unattributed pool onto this order.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setTab('fresh_issue')}
            className={`rounded-md border px-3 py-2 text-left transition-colors ${tab === 'fresh_issue' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
            data-testid="receive-jewellery-shortage-tab-fresh-issue"
          >
            <div className="flex items-center gap-2 text-sm font-medium">
              <PackagePlus size={14} className="text-emerald-700" />
              Issue Fresh Gold from Stock
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">
              Issue new gold to the goldsmith and attribute it to this order in one step.
            </div>
          </button>
        </div>

        {!pool ? (
          <div className="text-slate-500 py-6 text-center text-sm">Loading goldsmith&apos;s gold pool…</div>
        ) : tab === 'unattributed' ? (
          unattributedLines.length === 0 ? (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              {goldsmithName} has no unattributed gold available. Use the &quot;Issue Fresh Gold from Stock&quot; option instead.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">Pick a line to move from</Label>
                <div className="mt-1">
                  <SearchableSelect
                    items={unattributedLines}
                    value={pickedLineKey}
                    onValueChange={(v) => setPickedLineKey(v || '')}
                    itemToValue={(ln) => `${ln.issue_id}|${ln.line_id}`}
                    itemToLabel={(ln) => `${ln.purity_name} (${ln.purity_percent?.toFixed(2)}%) · ${formatGrams(ln.unattributed_grams)} g available · ${ln.issue_no}`}
                    searchKeys={['purity_name', 'issue_no']}
                    placeholder="Pick a line…"
                    searchPlaceholder="Search by purity / issue no…"
                    disabled={unattributedLines.length === 0}
                    testId="receive-jewellery-shortage-line-select"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="move-grams" className="text-xs text-slate-600">
                    Grams to move ({pickedLine?.purity_name} source purity)
                  </Label>
                  <Input
                    id="move-grams"
                    type="number"
                    step="0.001"
                    min="0"
                    max={pickedLine?.unattributed_grams || 0}
                    value={moveGrams}
                    onChange={(e) => setMoveGrams(e.target.value)}
                    className="h-9 mt-1 font-mono"
                    data-testid="receive-jewellery-shortage-grams-input"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Available: {formatGrams(pickedLine?.unattributed_grams || 0)} g
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600 self-end">
                  Equivalent in order purity ({orderPurityName}):{' '}
                  <span className="font-mono">
                    {pickedLine?.purity_percent && orderPurityPercent
                      ? formatGrams((+moveGrams || 0) * pickedLine.purity_percent / orderPurityPercent)
                      : '—'} g
                  </span>
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              Stock in hand: <span className="font-mono text-[var(--ink-navy)]">{formatGrams(pool.stock.grams_999)} g 999</span>
              · Avg rate: <span className="font-mono text-[var(--ink-navy)]">{formatINR(pool.stock.avg_rate_per_g_999)}/g</span>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Purity</Label>
                <div className="mt-1">
                  <SearchableSelect
                    items={pool.purities || []}
                    value={issuePurityId}
                    onValueChange={(v) => setIssuePurityId(v || '')}
                    itemToLabel={(p) => `${p.name} (${(p.percent || 0).toFixed(2)}%)`}
                    searchKeys={['name']}
                    placeholder="Select purity…"
                    searchPlaceholder="Search purity…"
                    testId="receive-jewellery-shortage-purity-select"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Grams</Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={issueGrams}
                  onChange={(e) => setIssueGrams(e.target.value)}
                  className="h-9 mt-1 font-mono"
                  data-testid="receive-jewellery-shortage-issue-grams-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Rate (₹/g 999)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={issueRate}
                  onChange={(e) => setIssueRate(e.target.value)}
                  className="h-9 mt-1 font-mono"
                  data-testid="receive-jewellery-shortage-issue-rate-input"
                />
              </div>
            </div>
            <div className="text-[11px] text-slate-500">
              Tip: enter grams in the selected purity. The system auto-deducts the 999 equivalent from your gold stock.
            </div>
          </div>
        )}

        <DialogFooter className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="receive-jewellery-shortage-cancel-button"
          >
            Cancel
          </Button>
          {tab === 'unattributed' ? (
            <Button
              className="luxury-button"
              onClick={handleMoveFromUnattributed}
              disabled={busy || !pickedLine || +moveGrams <= 0}
              data-testid="receive-jewellery-shortage-move-button"
            >
              {busy ? 'Moving…' : 'Move to Order'}
            </Button>
          ) : (
            <Button
              className="luxury-button"
              onClick={handleFreshIssue}
              disabled={busy || !issuePurityId || +issueGrams <= 0 || +issueRate <= 0}
              data-testid="receive-jewellery-shortage-issue-button"
            >
              {busy ? 'Issuing…' : 'Issue & Attribute'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Small presentational helpers
// ===========================================================================
function ReadOnlyStat({ label, value, icon, mono, highlight }) {
  return (
    <div className={`rounded-md border px-3 py-2 ${highlight ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className={`text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'text-amber-800 font-medium' : 'text-[var(--ink-navy)]'}`}>
        {value}
      </div>
    </div>
  );
}

function ReviewRow({ label, value, mono, highlight }) {
  return (
    <div className="flex justify-between items-center text-xs gap-3">
      <span className="text-slate-500">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} ${highlight ? 'text-amber-800 font-medium' : 'text-[var(--ink-navy)]'}`}>
        {value}
      </span>
    </div>
  );
}
